<template>
  <div class="about-us-page">
    <!-- 顶部导航组件 -->
    <TheHeader />

    <!-- 页面头部区域 -->
    <div class="page-hero">
      <div class="hero-background">
        <div class="hero-content">
          <div class="breadcrumb">
            <router-link to="/">首页</router-link>
            <span class="separator">/</span>
            <span class="current">关于我们</span>
          </div>
          <h1 class="hero-title">关于我们</h1>
          <p class="hero-subtitle">专注医药健康领域，致力于提供高品质产品与服务</p>
          <div class="hero-decoration">
            <div class="decoration-line"></div>
            <div class="decoration-dot"></div>
            <div class="decoration-line"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 页面内容区域 -->
    <div class="main-content">
      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
        <el-icon class="loading-icon"><Loading /></el-icon>
        <p>正在加载公司信息...</p>
      </div>

      <!-- 错误提示 -->
      <div v-else-if="error" class="error-container">
        <el-icon class="error-icon"><WarningFilled /></el-icon>
        <p>{{ error }}</p>
        <button class="retry-btn" @click="loadAboutUsData">重试</button>
      </div>

      <!-- 公司信息 -->
      <div v-else-if="aboutUsData" class="content-sections">
        <!-- 公司简介 -->
        <section class="intro-section">
          <div class="section-container">
            <div class="section-header">
              <div class="section-badge">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                公司简介
              </div>
              <h2 class="section-title">{{ aboutUsData.title || '我们的使命' }}</h2>
            </div>
            <div class="intro-content">
              <div class="intro-text">
                <div class="content-text" v-html="sanitizeHtml(aboutUsData.content) || getDefaultContent()"></div>
              </div>
              <div class="intro-image">
                <img :src="aboutUsData.image || require('@/assets/a8c414c60d46e434b72507890884b8f6.jpg')" alt="公司形象" />
                <div class="image-overlay">
                  <div class="overlay-content">
                    <h3>专业品质</h3>
                    <p>值得信赖</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- 发展历程 -->
        <section class="timeline-section" v-if="historyItems.length > 0">
          <div class="section-container">
            <div class="section-header">
              <div class="section-badge">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                发展历程
              </div>
              <h2 class="section-title">我们的成长足迹</h2>
              <p class="section-subtitle">见证每一个重要的发展节点</p>
            </div>
            <div class="timeline-wrapper">
              <div class="timeline-line"></div>
              <div class="timeline-item" v-for="(item, index) in historyItems" :key="index" :class="{ 'timeline-item-right': index % 2 === 1 }">
                <div class="timeline-marker">
                  <div class="marker-dot"></div>
                </div>
                <div class="timeline-card">
                  <div class="timeline-year">{{ item.year }}</div>
                  <h3 class="timeline-title">{{ item.title }}</h3>
                  <p class="timeline-content">{{ item.content }}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- 企业荣誉 -->
        <section class="honors-section" v-if="honorItems.length > 0">
          <div class="section-container">
            <div class="section-header">
              <div class="section-badge">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M5 16L3 5l5.5 1L12 4l3.5 2L21 5l-2 11H5zm2.7-2h8.6l.9-5.4-2.1-.4L12 6l-3.1 2.2-2.1.4L7.7 14z"/>
                </svg>
                企业荣誉
              </div>
              <h2 class="section-title">荣誉与认可</h2>
              <p class="section-subtitle">专业实力获得行业认可</p>
            </div>
            <div class="honors-grid">
              <div class="honor-card" v-for="(honor, index) in honorItems" :key="index">
                <div class="honor-image-wrapper">
                  <img :src="honor.image || placeholderImages.honor" :alt="honor.title" />
                  <div class="honor-overlay">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M5 16L3 5l5.5 1L12 4l3.5 2L21 5l-2 11H5zm2.7-2h8.6l.9-5.4-2.1-.4L12 6l-3.1 2.2-2.1.4L7.7 14z"/>
                    </svg>
                  </div>
                </div>
                <div class="honor-info">
                  <h3 class="honor-title">{{ honor.title }}</h3>
                  <p class="honor-year">{{ honor.year }}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- 联系信息 -->
        <section class="contact-section" v-if="aboutUsData.contact">
          <div class="section-container">
            <div class="section-header">
              <div class="section-badge">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
                联系我们
              </div>
              <h2 class="section-title">与我们取得联系</h2>
              <p class="section-subtitle">我们期待为您提供专业的服务</p>
            </div>
            <div class="contact-grid">
              <div class="contact-card">
                <div class="contact-icon-wrapper">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                </div>
                <div class="contact-content">
                  <h3>公司地址</h3>
                  <p>{{ aboutUsData.contact.address || '暂无地址信息' }}</p>
                </div>
              </div>
              <div class="contact-card">
                <div class="contact-icon-wrapper">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                </div>
                <div class="contact-content">
                  <h3>电子邮箱</h3>
                  <p>{{ aboutUsData.contact.email || '暂无邮箱信息' }}</p>
                </div>
              </div>
              <div class="contact-card">
                <div class="contact-icon-wrapper">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                  </svg>
                </div>
                <div class="contact-content">
                  <h3>联系电话</h3>
                  <p>{{ aboutUsData.contact.phone || '暂无电话信息' }}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
    
    <!-- 底部信息组件 -->
    <TheFooter />
  </div>
</template>

<script>
// 导入Element Plus组件
import { ElIcon } from 'element-plus'
import { Loading, WarningFilled } from '@element-plus/icons-vue'
import 'element-plus/dist/index.css'

// 导入占位图片
import { placeholderImages } from '@/assets/placeholder.js'

// 导入自定义组件
import TheHeader from '@/components/TheHeader.vue'
import TheFooter from '@/components/TheFooter.vue'

// 导入API
import { getAboutUs } from '@/api/config'

export default {
  name: 'AboutUs',
  components: {
    ElIcon,
    Loading,
    WarningFilled,
    TheHeader,
    TheFooter
  },
  data() {
    return {
      // 公司简介数据
      aboutUsData: null,
      loading: false,
      error: null,
      
      // 占位图片
      placeholderImages
    }
  },
  computed: {
    // 解析发展历程数据
    historyItems() {
      if (!this.aboutUsData || !this.aboutUsData.history) return [];
      
      try {
        if (typeof this.aboutUsData.history === 'string') {
          return JSON.parse(this.aboutUsData.history);
        }
        return this.aboutUsData.history;
      } catch (e) {
        console.error('解析发展历程数据失败:', e);
        return [];
      }
    },
    
    // 解析企业荣誉数据
    honorItems() {
      if (!this.aboutUsData || !this.aboutUsData.honors) return [];
      
      try {
        if (typeof this.aboutUsData.honors === 'string') {
          return JSON.parse(this.aboutUsData.honors);
        }
        return this.aboutUsData.honors;
      } catch (e) {
        console.error('解析企业荣誉数据失败:', e);
        return [];
      }
    }
  },
  methods: {
    // 加载公司简介数据
    async loadAboutUsData() {
      this.loading = true;
      this.error = null;

      try {
        const response = await getAboutUs();

        if (response.code === 1) {
          this.aboutUsData = response.data;
        } else {
          this.error = response.msg || '获取公司简介失败';
        }
      } catch (error) {
        console.error('获取公司简介错误:', error);
        this.error = '网络错误，请稍后重试';
      } finally {
        this.loading = false;
      }
    },

    // 安全的HTML处理
    sanitizeHtml(html) {
      if (!html) return '';
      return html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
                 .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
                 .replace(/on\w+="[^"]*"/gi, '');
    },

    // 获取默认内容
    getDefaultContent() {
      return `
        <p>我们是一家专注于医药健康领域的现代化企业，成立多年来始终坚持"质量第一，用户至上"的经营理念。</p>
        <p>公司拥有完善的质量管理体系和专业的技术团队，为广大客户提供安全、有效、优质的医药产品。我们的产品覆盖多个治疗领域，深受医疗机构和患者的信赖与好评。</p>
        <p>未来，我们将继续秉承专业、创新、诚信的企业精神，不断提升产品质量和服务水平，为人类健康事业贡献更大的力量。</p>
      `;
    }
  },
  created() {
    this.loadAboutUsData();
  }
}
</script>

<style scoped>
.about-us-page {
  font-family: 'Helvetica Neue', Arial, sans-serif;
  color: #333;
  line-height: 1.6;
  background: #f8fafc;
  min-height: 100vh;
}

/* 页面头部样式 */
.page-hero {
  position: relative;
  margin-top: 80px;
  overflow: hidden;
}

.hero-background {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  padding: 100px 0 80px;
}

.hero-background::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="hero-pattern" width="40" height="40" patternUnits="userSpaceOnUse"><circle cx="20" cy="20" r="2" fill="rgba(255,255,255,0.1)"/><circle cx="10" cy="10" r="1" fill="rgba(255,255,255,0.05)"/><circle cx="30" cy="30" r="1" fill="rgba(255,255,255,0.05)"/></pattern></defs><rect width="100" height="100" fill="url(%23hero-pattern)"/></svg>');
  opacity: 0.3;
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  text-align: center;
  color: white;
  position: relative;
  z-index: 1;
}

.breadcrumb {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 1rem;
  margin-bottom: 30px;
  opacity: 0.9;
}

.breadcrumb a {
  color: white;
  text-decoration: none;
  transition: opacity 0.3s ease;
}

.breadcrumb a:hover {
  opacity: 1;
}

.separator {
  opacity: 0.6;
}

.current {
  font-weight: 600;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 20px;
  text-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  letter-spacing: -1px;
}

.hero-subtitle {
  font-size: 1.4rem;
  opacity: 0.9;
  margin-bottom: 40px;
  font-weight: 300;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.hero-decoration {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
}

.decoration-line {
  width: 60px;
  height: 2px;
  background: rgba(255, 255, 255, 0.6);
}

.decoration-dot {
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
}

/* 主要内容区域 */
.main-content {
  position: relative;
  z-index: 10;
  margin-top: -40px;
  background: #f8fafc;
  border-radius: 30px 30px 0 0;
  padding-top: 80px;
}

/* 内容区域样式 */
.content-sections {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px 100px;
}

.section-container {
  margin-bottom: 100px;
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #3a8ee6, #42b983);
  color: white;
  padding: 10px 24px;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 25px;
}

.section-title {
  font-size: 2.8rem;
  font-weight: 800;
  color: #1a202c;
  margin-bottom: 16px;
  line-height: 1.2;
}

.section-subtitle {
  font-size: 1.2rem;
  color: #64748b;
  margin: 0;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

/* 加载和错误状态样式 */
.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  margin: 50px 0;
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
}

.loading-icon,
.error-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.loading-icon {
  color: #3a8ee6;
  animation: rotate 2s linear infinite;
}

.error-icon {
  color: #f56c6c;
}

.loading-container p,
.error-container p {
  font-size: 1.1rem;
  color: #64748b;
  margin: 0;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.retry-btn {
  margin-top: 20px;
  background: linear-gradient(135deg, #3a8ee6, #42b983);
  color: white;
  border: none;
  padding: 12px 28px;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 600;
}

.retry-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(58, 142, 230, 0.3);
}

/* 公司简介样式 */
.intro-section {
  background: white;
  border-radius: 24px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.intro-content {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 60px;
  align-items: center;
  padding: 60px;
}

.intro-text {
  padding-right: 20px;
}

.content-text {
  font-size: 1.2rem;
  line-height: 1.8;
  color: #475569;
}

.content-text :deep(p) {
  margin-bottom: 24px;
}

.intro-image {
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  transition: all 0.5s ease;
}

.intro-image:hover {
  transform: translateY(-10px);
  box-shadow: 0 30px 80px rgba(58, 142, 230, 0.2);
}

.intro-image img {
  width: 100%;
  height: 300px;
  object-fit: cover;
  transition: transform 0.8s ease;
}

.intro-image:hover img {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(58, 142, 230, 0.8), rgba(66, 185, 131, 0.8));
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.4s ease;
}

.intro-image:hover .image-overlay {
  opacity: 1;
}

.overlay-content {
  text-align: center;
  color: white;
}

.overlay-content h3 {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 8px;
}

.overlay-content p {
  font-size: 1.1rem;
  opacity: 0.9;
}

/* 发展历程样式 */
.history-timeline {
  position: relative;
  margin: 50px 0;
}

.history-timeline:before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 3px;
  background: linear-gradient(to bottom, #3a8ee6, #42b983);
  transform: translateX(-50%);
}

.timeline-item {
  display: flex;
  margin-bottom: 60px;
  position: relative;
}

.timeline-item:nth-child(odd) {
  padding-right: 50%;
  justify-content: flex-end;
}

.timeline-item:nth-child(even) {
  padding-left: 50%;
  justify-content: flex-start;
}

.timeline-year {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  background-color: #3a8ee6;
  color: white;
  padding: 8px 15px;
  border-radius: 25px;
  font-weight: bold;
  z-index: 2;
}

.timeline-content {
  background-color: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  width: 80%;
  margin-top: 30px;
}

.timeline-content h3 {
  color: #3a8ee6;
  margin-bottom: 15px;
}

/* 企业荣誉样式 */
.honors-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-top: 30px;
}

.honor-item {
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s;
}

.honor-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
}

.honor-image {
  height: 200px;
  overflow: hidden;
}

.honor-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.honor-title {
  padding: 15px 15px 5px;
  font-weight: 600;
  color: #333;
}

.honor-year {
  padding: 0 15px 15px;
  color: #777;
  font-size: 0.9rem;
}

/* 联系信息样式 */
.contact-info {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-top: 30px;
}

.contact-item {
  background-color: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  text-align: center;
  transition: all 0.3s;
}

.contact-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
}

.contact-icon {
  margin-bottom: 20px;
}

.contact-text h3 {
  font-size: 1.2rem;
  color: #3a8ee6;
  margin-bottom: 10px;
}

.contact-text p {
  color: #555;
}

/* 响应式设计 */
@media (max-width: 992px) {
  .section-content {
    flex-direction: column;
  }
  
  .about-image-container {
    margin-bottom: 30px;
    flex: 0 0 100%;
  }
  
  .honors-grid,
  .contact-info {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .timeline-item:nth-child(odd),
  .timeline-item:nth-child(even) {
    padding: 0 0 0 30px;
    justify-content: flex-start;
  }
  
  .history-timeline:before {
    left: 15px;
  }
  
  .timeline-year {
    left: 15px;
    transform: none;
  }
}

@media (max-width: 576px) {
  .page-title h1 {
    font-size: 2rem;
  }
  
  .honors-grid,
  .contact-info {
    grid-template-columns: 1fr;
  }
  
  .timeline-content {
    width: 100%;
  }
}
</style> 